// Inventario de la VITRINA (etapa 2, 29-sep-2026): conteo por turno, entradas y movimientos.
// Backend: routes/letsmove-vitrina.js (/api/letsmove/vitrina/*). El catálogo sigue en services/productsApi.
import { api } from '../../services/apiClient';
import { getSetting, putSetting } from '../../services/settings';

export type RespuestaPermisos = { platform?: boolean; modules?: { key: string; level?: string }[] };
const RANGO: Record<string, number> = { none: 0, view: 1, operate: 2, manage: 3 };
/** Qué puede hacer en la vitrina (MISMA regla que el servidor, routes/letsmove-vitrina.js):
 *  · editar catálogo = vitrina manage · anotar entrada («Llegó mercancía») = vitrina ≥ operate
 *  · CONTAR = vitrina ≥ operate, o ver la vitrina + manejar la caja (cashbox ≥ operate) — la cajera,
 *    desde el 1-oct-2026. Sin lista de módulos (respuesta antigua) no se esconde nada: decide el servidor. */
export function permisosVitrina(r: RespuestaPermisos | null | undefined) {
  if (!r || r.platform || !Array.isArray(r.modules)) return { editar: true, entrada: true, contar: true };
  const nv = (k: string) => RANGO[r.modules!.find((m) => m.key === k)?.level || 'none'] || 0;
  const v = nv('vitrina');
  return { editar: v >= 3, entrada: v >= 2, contar: v >= 2 || (v >= 1 && nv('cashbox') >= 2) };
}

export interface ConteoDiff { product_id: number; name: string; expected: number | null; counted: number; diff: number | null }
export interface ConteoTurno {
  count_id: number; at: string; by: string | null;
  lines_counted: number; lines_diff: number; note: string | null;
  diffs: ConteoDiff[]; counts_in_shift: number;
}
export interface UltimoConteo {
  count_id: number; at: string; by: string | null; lines_counted: number; lines_diff: number;
  cashbox_id: number | null; shift_label: string | null; note?: string | null;
}
export interface ConteoDetalle extends UltimoConteo { lines: ConteoDiff[] }
export interface VitrinaStatus {
  enabled: boolean;
  require_count_on_close: boolean;
  /** Último conteo del negocio, sea del turno que sea. null = nunca se ha contado. */
  last_count: UltimoConteo | null;
  cashbox: { cashbox_id: number; shift_label: string | null; opened_at: string; opened_by_name: string | null } | null;
  count: ConteoTurno | null;
  /** Hay turno abierto y nadie ha contado: es lo que enciende el aviso. */
  needs_count: boolean;
  /** Productos con ventas, fiados o entradas desde su último conteo: el conteo los enseña primero. */
  moved: number[];
}
export interface Movimiento {
  id: number; kind: 'sale' | 'credit' | 'return' | 'entry' | 'adjust' | 'count';
  qty: number; applied: number; stock_after: number | null;
  note: string | null; created_at: string; returned_at: string | null; user_name: string | null;
}
export interface VitrinaInventarioCfg { enabled: boolean; require_count_on_close: boolean }

export function getVitrinaStatus() { return api.get<VitrinaStatus>('/vitrina/status'); }
export function guardarConteo(lines: { product_id: number; counted: number }[], note?: string) {
  return api.post<{ ok: true; count_id: number; lines: ConteoDiff[]; lines_counted: number; lines_diff: number; cashbox_id: number | null }>(
    '/vitrina/count', { lines, note: note || null });
}
export function registrarEntrada(product_id: number, qty: number, note?: string) {
  return api.post<{ ok: true; stock: number | null }>('/vitrina/entry', { product_id, qty, note: note || null });
}
export function getHistorialConteos(limit = 60) { return api.get<{ data: UltimoConteo[] }>(`/vitrina/counts?limit=${limit}`); }
export function getConteo(id: number) { return api.get<ConteoDetalle>(`/vitrina/counts/${id}`); }
export function getMovimientos(product_id: number | string) {
  return api.get<{ data: Movimiento[] }>(`/vitrina/movements?product_id=${product_id}`);
}
export async function getInventarioCfg(): Promise<VitrinaInventarioCfg> {
  const r = await getSetting('vitrina_inventory');
  try {
    const o = JSON.parse(r.value);
    return { enabled: o.enabled === true, require_count_on_close: o.enabled === true && o.require_count_on_close === true };
  } catch { return { enabled: false, require_count_on_close: false }; }
}
export function putInventarioCfg(cfg: VitrinaInventarioCfg) {
  return putSetting('vitrina_inventory', JSON.stringify(cfg));
}

/** Aviso entre pantallas: algo cambió en la vitrina o en el turno (se abrió la caja, se vendió, se
 *  contó). Quien enseña existencias o el aviso de conteo se vuelve a cargar. `abrirConteo` pide
 *  además que el conteo se abra solo (recién abierta la caja). */
export const EVENTO_VITRINA = 'lm:vitrina';
export function avisarVitrina(detail: { abrirConteo?: boolean } = {}) {
  window.dispatchEvent(new CustomEvent(EVENTO_VITRINA, { detail }));
}
